import { API_PREFIX, CSRF_HEADER, CSRF_HEADER_VALUE, type ApiErrorBody } from '@quds/shared';

/**
 * HTTP client for the isolated Central Admin API (proxied by Vite to
 * 127.0.0.1:5311). It never reaches the production quds-pos-cloud backend.
 * Authentication is an HttpOnly session cookie the browser sends
 * automatically; scripts cannot read it.
 */

export interface FieldIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level validation issues, keyed by dotted path. */
  get fieldIssues(): FieldIssue[] {
    return Array.isArray(this.details) ? (this.details as FieldIssue[]).filter((d) => d && typeof d.path === 'string') : [];
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Notified whenever an authenticated request is rejected with 401 (session ended). */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

type Query = Record<string, string | number | boolean | undefined | null>;

export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  options: { body?: unknown; query?: Query; formData?: FormData } = {},
): Promise<T> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  const url = `${API_PREFIX}${path}${qs ? `?${qs}` : ''}`;
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') headers[CSRF_HEADER] = CSRF_HEADER_VALUE;
  if (options.formData === undefined && options.body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: 'same-origin',
      body:
        options.formData !== undefined
          ? options.formData
          : options.body === undefined
            ? undefined
            : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'The Central Admin API could not be reached. Check that it is running.');
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!res.ok) {
    const err = (json as ApiErrorBody | null)?.error;
    const error = new ApiError(res.status, err?.code ?? 'HTTP_ERROR', err?.message ?? `Admin API responded with HTTP ${res.status}`, err?.details);
    if (res.status === 401 && error.code === 'UNAUTHENTICATED') unauthorizedListeners.forEach((l) => l());
    throw error;
  }
  return json as T;
}

export const apiGet = <T>(path: string, query?: Query) => apiRequest<T>('GET', path, { query });
export const apiPost = <T>(path: string, body?: unknown) => apiRequest<T>('POST', path, { body: body ?? {} });
export const apiPatch = <T>(path: string, body: unknown) => apiRequest<T>('PATCH', path, { body });
export const apiDelete = <T>(path: string, body?: unknown) => apiRequest<T>('DELETE', path, { body: body ?? {} });
export const apiPostForm = <T>(path: string, formData: FormData) => apiRequest<T>('POST', path, { formData });

/** User-facing message for any error thrown by the data layer. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof ApiError) {
    // 502 PRODUCTION_ERROR carries a safe operator message from Integration mapping
    // (e.g. missing Production Reveal route). Do not replace it with a generic 5xx string.
    if (err.status === 502 && err.code === 'PRODUCTION_ERROR' && err.message) return err.message;
    if (err.status >= 500) return 'The server could not complete the request. Please try again.';
    return err.message;
  }
  return fallback;
}
